import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { authHeader } from '../../utils/headers';
import { toastHelper } from '../../utils/toast-helper';

const API_BASE = import.meta.env.VITE_APIURL || '';

export const EXPORT_STATUS = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    DONE: 'done',
    FAILED: 'failed',
    EXPIRED: 'expired',
};

// Les collections API Platform arrivent en ld+json : on aplatit hydra:member.
const toArray = (response) => {
    if (Array.isArray(response)) return response;
    return response?.['hydra:member'] ?? response?.member ?? [];
};

export const exportsApi = createApi({
    reducerPath: 'exportsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE,
        prepareHeaders: (headers) => {
            const token = authHeader();
            if (typeof token === 'string' && token) {
                headers.set('authorization', token);
            }
            // Règle CLAUDE.md n°1 : forcer ld+json pour garder les @id.
            headers.set('accept', 'application/ld+json');
            return headers;
        },
    }),
    tagTypes: ['Exports', 'PendingExports'],
    endpoints: (builder) => ({
        // Liste complète « Mes exports » (le back cloisonne sur l'utilisateur courant).
        getMyExports: builder.query({
            query: ({ statuts, patrimoine, depuis } = {}) => {
                const params = new URLSearchParams();
                (statuts || []).forEach((s) => params.append('statut[]', s));
                if (patrimoine) params.set('patrimoine', patrimoine);
                if (depuis) params.set('depuis', depuis);
                const qs = params.toString();
                return qs ? `/exports?${qs}` : '/exports';
            },
            transformResponse: toArray,
            providesTags: ['Exports'],
        }),

        // Exports en vol de l'utilisateur — la requête du watcher (polling conditionnel).
        getMyPendingExports: builder.query({
            query: () => '/exports?statut[]=pending&statut[]=processing',
            transformResponse: toArray,
            providesTags: ['PendingExports'],
        }),

        // Détail d'un export (utilisé par le watcher pour connaître le statut final).
        getExport: builder.query({
            query: (uuid) => `/exports/${uuid}`,
        }),

        createExport: builder.mutation({
            query: (values) => ({
                url: '/exports',
                method: 'POST',
                headers: { 'content-type': 'application/ld+json' },
                // Règle CLAUDE.md n°2 : sérialiser soi-même le body.
                body: JSON.stringify(values),
            }),
            invalidatesTags: ['Exports', 'PendingExports'],
        }),

        regenerateExport: builder.mutation({
            query: (uuid) => ({
                url: `/exports/${uuid}/regenerate`,
                method: 'POST',
                headers: { 'content-type': 'application/ld+json' },
                body: JSON.stringify({}),
            }),
            invalidatesTags: ['Exports', 'PendingExports'],
        }),

        // Suppression volontaire (le back refuse en 409 les exports en cours).
        // Réponse JSON simple (pas de 204) — pas de body à sérialiser.
        deleteExport: builder.mutation({
            query: (uuid) => ({
                url: `/exports/${uuid}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Exports'],
        }),

        // Marque tous les done non lus comme consultés → badge à zéro.
        consulterExports: builder.mutation({
            query: () => ({
                url: '/exports/consulter',
                method: 'POST',
                headers: { 'content-type': 'application/ld+json' },
                body: JSON.stringify({}),
            }),
            invalidatesTags: ['Exports'],
        }),
    }),
});

export const {
    useGetMyExportsQuery,
    useGetMyPendingExportsQuery,
    useCreateExportMutation,
    useRegenerateExportMutation,
    useDeleteExportMutation,
    useConsulterExportsMutation,
} = exportsApi;

/**
 * Téléchargement en deux temps : download-url (JWT + propriétaire) → URL signée
 * 5 min → window.open. À appeler depuis un geste utilisateur (clic) pour ne pas
 * déclencher le bloqueur de popups.
 */
export async function downloadExport(uuid) {
    try {
        const response = await fetch(`${API_BASE}/exports/${uuid}/download-url`, {
            headers: {
                Authorization: authHeader(),
                Accept: 'application/json',
            },
        });
        const data = await response.json();
        if (response.ok && data?.url) {
            window.open(data.url, '_blank');
        } else {
            toastHelper.handleError(null, data?.message || 'Téléchargement indisponible.');
        }
    } catch (e) {
        toastHelper.handleError(e, 'Téléchargement indisponible.');
    }
}
